import { fetcher } from "@website/fetcher/fetcher";
import { useMutation } from "@website/fetcher/use-fetcher";
import { useJwtToken, useLoginStatus } from "@website/hooks/auth-manager";
import { MaterialIcon } from "@website/icons/material";
import { LoginStatus } from "@website/store/store";
import { APIs } from "@website/ticket-ui/visa/apis";
import { PeoplePopover } from "@website/ticket-ui/visa/peoplePopover";
import type { Country, VisaOrder, VisaTypesResponse } from "@website/ticket-ui/visa/types";
import { ToggleButtons } from "@website/ticket-ui/visa/visaForm/toggleButton";
import { VisaTypePopover } from "@website/ticket-ui/visa/visaTypePopover";
import { WEBENGAGE_EVENTS } from "@website/tracking/tracking.types";
import { addEventToWebengage } from "@website/tracking/tracking.webengage";
import { Button } from "@website/ui/button";
import { Modal } from "@website/ui/modal";
import { Skeleton } from "@website/ui/skeleton";
import { useSnackbar } from "@website/ui/snackbar";
import { Switch } from "@website/ui/switch";
import { TextField } from "@website/ui/text-field";
import { getChannel } from "@website/utils/channel/get-channel";
import { numberConvert } from "@website/utils/number-convert";
import { useLoginModal } from "@website/widget/login";
import { useRouter } from "next/navigation";
import { useState } from "react";
export type VisaProperties = {
  visaTypes: VisaTypesResponse["Items"];
  selectedCountry: Country;
};
export type FormItems = {
  adult?: number;
  child?: number;
  person?: number;
  phoneNumber: string;
  visaType: number;
  timeType?: string;
  isExtending?: boolean;
};
const generateInitialState = (formConditions: Country["form"], visaTypes: VisaTypesResponse["Items"]) => {
  const getPersonType = {
    childAndAdult: {
      adult: 1,
      child: 0
    },
    person: {
      person: 1
    }
  };
  return {
    ...getPersonType[formConditions.personType],
    phoneNumber: "",
    visaType: visaTypes[0].code,
    ...(formConditions.hasTimeType && {
      timeType: "normal"
    }),
    ...(formConditions.hasExtention && {
      isExtending: false
    })
  };
};
export const VisaForm = (properties: VisaProperties) => {
  const {
    enqueue
  } = useSnackbar();
  const router = useRouter();
  const {
    onLoginModalOpen
  } = useLoginModal();
  const {
    selectedCountry,
    visaTypes
  } = properties;
  const [showPeople, setShowPeople] = useState(false);
  const [showVisaType, setShowVisaType] = useState(false);
  const {
    form: formConditions
  } = selectedCountry;
  const {
    loginStatus,
    isLoggedIn
  } = useLoginStatus();
  const [formValues, setFormValues] = useState<FormItems>(generateInitialState(formConditions, visaTypes));
  type FieldNames = keyof typeof formValues;
  type FieldValues = (typeof formValues)[FieldNames];
  const handleValueChange = (value: FieldValues, name: FieldNames) => {
    setFormValues(previousState => ({
      ...previousState,
      [name]: value
    }));
  };
  const getPeopleString = () => {
    if (formConditions.personType === "childAndAdult") {
      const adult = numberConvert(formValues.adult, {
        locale: "fa"
      });
      const child = numberConvert(formValues.child, {
        locale: "fa"
      });
      if (!formValues.child) return `${adult} بزرگسال`;
      return `${adult} بزرگسال، ${child} کودک`;
    }
    if (formConditions.personType === "person") {
      const person = numberConvert(formValues.person, {
        locale: "fa"
      });
      return `${person} نفر`;
    }
  };
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const {
    mutateAsync,
    isPending
  } = useMutation({
    mutationFn: (data: VisaOrder) => fetcher({
      url: APIs.VisaOrder,
      method: "POST",
      data
    })
  });
  const {
    phoneNumber
  } = useJwtToken();
  const handleOTPSuccess = () => {
    const requestData: VisaOrder = {
      ...(formValues.adult && {
        adultCount: formValues.adult
      }),
      ...(formValues.child && {
        childCount: formValues.child
      }),
      ...(formValues.person && {
        personCount: formValues.person
      }),
      phoneNumber: phoneNumber.replace("+98", "0"),
      visaTypeId: formValues.visaType,
      ...(formValues.isExtending !== null && {
        isRenewal: formValues.isExtending
      }),
      ...(formValues.timeType && {
        isInstantaneous: formValues.timeType !== "normal"
      })
    };
    mutateAsync(requestData).then(() => {
      try {
        addEventToWebengage(WEBENGAGE_EVENTS.FLIGHT_INTL_VISA_SUBMIT, {
          channel: getChannel(),
          visaType: visaTypes.find(item => item.code === formValues.visaType)?.title,
          childNumber: formValues.child,
          adultNumber: formValues.adult,
          isInstantaneous: formValues.timeType !== "normal",
          isRenewal: formValues.isExtending
        });
      } catch {
        /* empty */
      }
      router.push(`/successful`);
    }).catch(() => {
      enqueue(`متاسفانه درخواست شما ثبت نشد لطفا دوباره امتحان کنید.`, "error", "two-lines");
    });
  };
  const handleForm = () => {
    if (isLoggedIn) {
      setShowConfirmationModal(true);
    } else {
      onLoginModalOpen().then(() => {
        setShowConfirmationModal(true);
      });
    }
  };
  const findVisaTypeTitle = (code: number) => visaTypes.find(item_0 => item_0.code === code)?.title;
  return <div className="flex flex-col gap-y-6 pt-6">
      <div className="flex content-center justify-between md:justify-normal md:gap-x-6">
        {formConditions.hasTimeType && <ToggleButtons activeItem={formValues.timeType} items={[{
        label: "عادی",
        value: "normal"
      }, {
        label: "فوری",
        value: "urgent"
      }]} onChange={v => handleValueChange(v, "timeType")} />}
        {formConditions.hasExtention && <Switch checked={formValues.isExtending} color="secondary" label="تمدید ویزا" onChange={v_0 => handleValueChange(v_0.target.checked, "isExtending")} />}
      </div>
      <div className="flex flex-col gap-y-6 pt-6 md:w-full md:flex-row md:gap-x-6">
        {loginStatus === LoginStatus.Pending ? <>
            <div className="relative flex-1">
              <Skeleton height={56} />
            </div>
            <div className="relative flex-1">
              <Skeleton height={56} />
            </div>
            <div className="relative flex-1">
              <Skeleton height={56} />
            </div>
          </> : <>
            <div className="relative flex-1">
              <TextField fullWidth label="تعداد نفرات" onClick={() => setShowPeople(true)} readOnly value={getPeopleString()} />
              <PeoplePopover onChange={(v_1, name_0) => handleValueChange(v_1, name_0)} onClose={() => setShowPeople(false)} open={showPeople} type={formConditions.personType} value={{
            adult: formValues.adult as number,
            child: formValues.child as number,
            person: formValues.person
          }} />
            </div>
            {formConditions.hasVisaType && <div className="relative flex-1">
                <TextField EndIcon={<MaterialIcon name="arrow_drop_down" />} fullWidth label="نوع ویزا" onClick={() => setShowVisaType(true)} readOnly value={findVisaTypeTitle(formValues.visaType as number)} />
                <VisaTypePopover onChange={v_2 => handleValueChange(v_2, "visaType")} onClose={() => setShowVisaType(false)} open={showVisaType} visaTypes={visaTypes} />
              </div>}
            <Button className="max-sm:!h-12 max-sm:w-full" loading={isPending} onClick={handleForm} size="x-large">
              ثبت درخواست
            </Button>
          </>}
      </div>

      <Modal animate="bottom" mobileHeight="auto" onClose={() => setShowConfirmationModal(false)} open={showConfirmationModal} showCloseButton title={`ثبت درخواست ویزا ${selectedCountry.faName}`}>
        <div className="mt-6 flex flex-col gap-6 lg:w-[400px]">
          <p className="text-body-2">
            با ثبت درخواست، کارشناسان ما در اسرع وقت با شما تماس خواهند گرفت و
            شما را برای خرید ویزا راهنمایی خواهند کرد.
          </p>
          <Button className="mt-4" color="secondary" fullWidth loading={isPending} onClick={handleOTPSuccess} size="large">
            ثبت درخواست
          </Button>
        </div>
      </Modal>
    </div>;
};